export enum FilterCategory {
  PERSONAL = 'Personal Filters',
  TRANSACTION = 'Transaction Filters',
  CAMPAIGN = 'Campaign Filters',
  COUPON = 'Coupon Filters',
  OTHER = 'Other Filters',
}

export interface FilterTemplateDefinition {
  selectionType: string;
  label: string;
  category: FilterCategory;
  defaults: { [key: string]: any };
  apiKeys: string[];
}

export interface FilterData extends FilterTemplateDefinition {
  displayValue?: string;
  headerPlaceholder?: string;
  order?: number;
  instanceId?: number;
  [key: string]: any;
}

export const LOYALTY_TEMPLATES_DATA: FilterTemplateDefinition[] = [
  {
    selectionType: 'CHILD_NAMEDAY',
    label: 'Child Nameday',
    category: FilterCategory.PERSONAL,
    defaults: {
      level: -1,
      order: 0,
      operator: 'AND',
      daysAfter: 0,
      useNameDayService: false,
      maxChildsAge: 18,
      namedayDefined: null,
      displayFormat: "{daysAfter} after/before/on the child's nameday",
      headerPlaceholder: "On the child's nameday",
    },
    apiKeys: [
      'selectionType',
      'level',
      'operator',
      'daysAfter',
      'useNameDayService',
      'maxChildsAge',
      'namedayDefined',
    ],
  },
  {
    selectionType: 'CHILD_BIRTHDATE',
    label: 'Child Birthdate',
    category: FilterCategory.PERSONAL,
    defaults: {
      level: -1,
      order: 0,
      operator: 'AND',
      daysAfter: 0,
      maxChildsAge: 18,
      displayFormat: "{daysAfter} after/before/on the child's birthday",
      headerPlaceholder: "On the child's birthday",
    },
    apiKeys: ['maxChildsAge', 'selectionType', 'level', 'operator', 'daysAfter'],
  },
  {
    selectionType: 'MEMBER_EXCLUDE',
    label: 'Have already received this campaign',
    category: FilterCategory.CAMPAIGN,
    defaults: {
      level: -1,
      order: 0,
      operator: 'AND',
      days: null,
      months: null,
      year: false,
      month: false,
      searchCampaignExecutionsBeforeTheCalculatedDate: false,
      displayValue: 'Configuring...',
      headerPlaceholder: 'Configuring...',
    },
    apiKeys: [
      'selectionType',
      'level',
      'operator',
      'days',
      'months',
      'year',
      'month',
      'searchCampaignExecutionsBeforeTheCalculatedDate',
    ],
  },
  {
    selectionType: 'MEMBER_VALID_MOBILE',
    label: 'Valid mobile',
    category: FilterCategory.PERSONAL,
    defaults: {
      level: -1,
      order: 0,
      operator: 'AND',
      displayValue: 'Enabled',
      headerPlaceholder: 'Enabled',
    },
    apiKeys: ['selectionType', 'level', 'operator'],
  },
  {
    selectionType: 'MEMBER_NAMED_FLAG',
    label: 'Consents',
    category: FilterCategory.PERSONAL,
    defaults: {
      level: -1,
      order: 0,
      operator: 'AND',
      flag: false,
      namedFlag: null,
      flagChannel: null,
      displayValue: 'Choose a consent',
      headerPlaceholder: 'Choose a consent',
    },
    apiKeys: ['selectionType', 'level', 'operator', 'flag', 'namedFlag', 'flagChannel'],
  },
  {
    selectionType: 'MEMBER_BIRTHDATE',
    label: 'Birthday',
    category: FilterCategory.PERSONAL,
    defaults: {
      level: -1,
      order: 0,
      operator: 'AND',
      daysAfter: 0,
      displayFormat: "On the member's birthday",
      headerPlaceholder: "On the member's birthday",
    },
    apiKeys: ['selectionType', 'level', 'operator', 'daysAfter'],
  },
  {
    selectionType: 'MEMBER_ATTRIBUTES',
    label: 'Attributes',
    category: FilterCategory.OTHER,
    defaults: {
      level: -1,
      order: 0,
      operator: 'AND',
      attributeFilters: [{ attributeType: null, attributeName: null, attributeValue: null }],
      displayValue: 'Choose attributes',
      headerPlaceholder: 'Choose attributes',
    },
    apiKeys: ['selectionType', 'level', 'operator', 'attributeFilters'],
  },
  {
    selectionType: 'MEMBER_POINTS',
    label: 'Points',
    category: FilterCategory.OTHER,
    defaults: {
      level: -1,
      order: 0,
      operator: 'AND',
      memberPoints: 0,
      comperator: 'GREATER_THAN',
      displayValue: 'more than 0 points',
      headerPlaceholder: 'more than 0 points',
    },
    apiKeys: ['selectionType', 'level', 'operator', 'memberPoints', 'comperator'],
  },
  {
    selectionType: 'MEMBER_REGISTRATION_DATE',
    label: 'Member registration date',
    category: FilterCategory.OTHER,
    defaults: {
      level: -1,
      order: 0,
      operator: 'AND',
      days: 0,
      months: 0,
      registrationDate: null,
      comperator: 'GREATER_THAN',
      durationType: 'daysMonths',
      displayValue: 'more than 0 days and 0 months',
      headerPlaceholder: 'more than 0 days and 0 months',
    },
    apiKeys: [
      'selectionType',
      'level',
      'operator',
      'days',
      'months',
      'registrationDate',
      'comperator',
    ],
  },
  {
    selectionType: 'MEMBER_ACTIVE_LOYALTY_CARD',
    label: 'Active loyalty card',
    category: FilterCategory.PERSONAL,
    defaults: {
      level: -1,
      order: 0,
      operator: 'AND',
      displayValue: 'Enabled',
      headerPlaceholder: 'Enabled',
    },
    apiKeys: ['selectionType', 'level', 'operator'],
  },
  {
    selectionType: 'MEMBER_VALID_EMAIL',
    label: 'Valid e-mail',
    category: FilterCategory.PERSONAL,
    defaults: {
      level: -1,
      order: 0,
      operator: 'AND',
      displayValue: 'Enabled',
      headerPlaceholder: 'Enabled',
    },
    apiKeys: ['selectionType', 'level', 'operator'],
  },
  {
    selectionType: 'MEMBER_POINTLOG_EXPIRATION',
    label: 'Pointlog expiration',
    category: FilterCategory.OTHER,
    defaults: {
      level: -1,
      order: 0,
      operator: 'AND',
      fromDays: 0,
      fromMonths: 0,
      fromExpirationDate: null,
      untilDays: 0,
      untilMonths: 0,
      untilExpirationDate: null,
      durationType: 'daysMonths',
      displayValue:
        'Pointlog expiration day from 0 Pointlog expiration months from 0 - Pointlog expiration day until 0 Pointlog expiration months until 0',
      headerPlaceholder: 'Configure pointlog expiration',
    },
    apiKeys: [
      'selectionType',
      'level',
      'operator',
      'fromDays',
      'fromMonths',
      'fromExpirationDate',
      'untilDays',
      'untilMonths',
      'untilExpirationDate',
    ],
  },
  {
    selectionType: 'MEMBER_INVOICE',
    label: 'Invoice',
    category: FilterCategory.TRANSACTION,
    defaults: {
      level: -1,
      order: 0,
      operator: 'AND',
      comperator: null,
      durationType: null,
      days: null,
      months: null,
      invoiceDate: null,
      invoiceFields: [{ fieldName: null, value: null }],
      displayValue: 'Choose invoice fields',
      headerPlaceholder: 'Choose invoice fields',
    },
    apiKeys: [
      'selectionType',
      'level',
      'operator',
      'comperator',
      'durationType',
      'days',
      'months',
      'invoiceDate',
      'invoiceFields',
    ],
  },
  {
    selectionType: 'MEMBER_INVOICE_INACTIVE',
    label: 'No transaction',
    category: FilterCategory.TRANSACTION,
    defaults: {
      level: -1,
      order: 0,
      operator: 'AND',
      days: null,
      months: null,
      storeOperator: 'ALL',
      storeIds: [],
      displayValue: 'Set days or months',
      headerPlaceholder: 'Set days or months',
    },
    apiKeys: ['selectionType', 'level', 'operator', 'days', 'months', 'storeOperator', 'storeIds'],
  },
  {
    selectionType: 'MEMBER_GENDER_SELECTION',
    label: 'Gender',
    category: FilterCategory.PERSONAL,
    defaults: {
      level: -1,
      order: 0,
      operator: 'AND',
      genderList: [],
      displayValue: 'Choose a gender type',
      headerPlaceholder: 'Choose a gender type',
    },
    apiKeys: ['selectionType', 'level', 'operator', 'genderList'],
  },
  {
    selectionType: 'MEMBER_CATEGORY',
    label: 'Member category',
    category: FilterCategory.OTHER,
    defaults: {
      level: -1,
      order: 0,
      operator: 'AND',
      tableMemberCategoryId: 1,
      displayValue: 'Select a category',
      headerPlaceholder: 'Select a category',
    },
    apiKeys: ['selectionType', 'tableMemberCategoryId', 'level', 'operator'],
  },
  {
    selectionType: 'MEMBER_BOUNCED_EMAIL',
    label: 'Bounced e-mail',
    category: FilterCategory.PERSONAL,
    defaults: {
      level: -1,
      order: 0,
      operator: 'AND',
      displayValue: 'Enabled',
      headerPlaceholder: 'Enabled',
    },
    apiKeys: ['selectionType', 'level', 'operator'],
  },
  {
    selectionType: 'MEMBER_STATUS',
    label: 'Member status',
    category: FilterCategory.PERSONAL,
    defaults: {
      level: -1,
      order: 0,
      operator: 'AND',
      memberStatusIdList: [],
      displayValue: 'Select member statuses',
      headerPlaceholder: 'Select member statuses',
    },
    apiKeys: ['selectionType', 'memberStatusIdList', 'level', 'operator'],
  },
  {
    selectionType: 'MEMBER_NAMEDAY',
    label: 'Nameday',
    category: FilterCategory.PERSONAL,
    defaults: {
      level: -1,
      order: 0,
      operator: 'AND',
      daysAfter: 0,
      useNameDayService: false,
      namedayDefined: false,
      displayFormat: "On the person's nameday",
      headerPlaceholder: "On the person's nameday",
    },
    apiKeys: [
      'selectionType',
      'level',
      'operator',
      'daysAfter',
      'useNameDayService',
      'namedayDefined',
    ],
  },
  {
    selectionType: 'MEMBER_AGE',
    label: 'Age group',
    category: FilterCategory.PERSONAL,
    defaults: {
      level: -1,
      order: 0,
      operator: 'AND',
      minAge: null,
      maxAge: null,
      displayFormat: 'Age between {minAge} and {maxAge}',
      headerPlaceholder: 'Choose age range',
    },
    apiKeys: ['selectionType', 'level', 'operator', 'minAge', 'maxAge'],
  },
  {
    selectionType: 'MEMBER_REGISTRATION_STORE',
    label: 'Registration store',
    category: FilterCategory.PERSONAL,
    defaults: {
      level: -1,
      order: 0,
      operator: 'AND',
      registrationStoreId: null,
      displayValue: 'Select a store',
      headerPlaceholder: 'Select a store',
    },
    apiKeys: ['selectionType', 'level', 'operator', 'registrationStoreId'],
  },
  {
    selectionType: 'MEMBER_COUPON',
    label: 'Coupons',
    category: FilterCategory.COUPON,
    defaults: {
      level: -1,
      order: 0,
      operator: 'AND',
      couponSetId: null,
      couponActiveFunctionality: false,
      couponActiveLookBackDays: null,
      couponActiveLookFrontDays: null,
      validFrom: null,
      validUntil: null,
      couponUsedFunctionality: false,
      couponUsedLookBackDays: null,
      couponUsedLookFrontDays: null,
      usedFrom: null,
      usedUntil: null,
      couponSetAttributeFilters: [],
      couponAttributeFilters: [],
      couponSetIdList: [],
      displayValue: 'Configure coupon filter',
      headerPlaceholder: 'Configure coupon filter',
    },
    apiKeys: [
      'selectionType',
      'level',
      'operator',
      'couponSetId',
      'couponActiveFunctionality',
      'couponActiveLookBackDays',
      'couponActiveLookFrontDays',
      'validFrom',
      'validUntil',
      'couponUsedFunctionality',
      'couponUsedLookBackDays',
      'couponUsedLookFrontDays',
      'usedFrom',
      'usedUntil',
      'couponSetAttributeFilters',
      'couponAttributeFilters',
      'couponSetIdList',
    ],
  },
  {
    selectionType: 'COUPON_EXPIRATION',
    label: 'Coupon expiration',
    category: FilterCategory.COUPON,
    defaults: {
      level: -1,
      order: 0,
      operator: 'AND',
      couponSetId: null,
      daysToExpiration: null,
      expirationDate: null,
      durationType: '',
      displayValue: 'Configure expiration',
      headerPlaceholder: 'Configure expiration',
    },
    apiKeys: [
      'selectionType',
      'level',
      'operator',
      'couponSetId',
      'daysToExpiration',
      'expirationDate',
      'durationType',
    ],
  },
  {
    selectionType: 'MEMBERS_LOYALTY_POINT_LOGS',
    label: 'Pointlogs',
    category: FilterCategory.OTHER,
    defaults: {
      level: -1,
      order: 0,
      operator: 'AND',
      days: 0,
      months: 0,
      durationType: null,
      loyaltyPointLogDate: null,
      loyaltyPointLogSourceIds: [],
      comperator: 'LESS_THAN_OR_EQUAL',
      displayValue: 'less than or equals 0 days and 0 months',
      headerPlaceholder: 'Configure pointlogs',
    },
    apiKeys: [
      'selectionType',
      'level',
      'operator',
      'days',
      'months',
      'durationType',
      'loyaltyPointLogDate',
      'loyaltyPointLogSourceIds',
      'comperator',
    ],
  },
  {
    selectionType: 'MEMBER_MEMBER_SOURCE',
    label: 'Member source',
    category: FilterCategory.PERSONAL,
    defaults: {
      level: -1,
      order: 0,
      operator: 'AND',
      memberSourceIdList: [],
      displayValue: 'Select member sources',
      headerPlaceholder: 'Select member sources',
    },
    apiKeys: ['selectionType', 'memberSourceIdList', 'level', 'operator'],
  },
  {
    selectionType: 'MEMBER_PREFERRED_LANGUAGE',
    label: 'Preferred language',
    category: FilterCategory.PERSONAL,
    defaults: {
      level: -1,
      order: 0,
      operator: 'AND',
      languageIds: [],
      displayValue: 'Select languages',
      headerPlaceholder: 'Select languages',
    },
    apiKeys: ['selectionType', 'languageIds', 'level', 'operator'],
  },
  {
    selectionType: 'MEMBER_PERSON_ATTRIBUTES',
    label: 'Customer attributes',
    category: FilterCategory.OTHER,
    defaults: {
      level: -1,
      order: 0,
      operator: 'AND',
      attributeFilters: [{ attributeType: null, attributeName: null, attributeValue: null }],
      displayValue: 'Choose attributes',
      headerPlaceholder: 'Choose attributes',
    },
    apiKeys: ['selectionType', 'level', 'operator', 'attributeFilters'],
  },
  {
    selectionType: 'MEMBER_MOBILE_LOGIN',
    label: 'Member mobile login',
    category: FilterCategory.OTHER,
    defaults: {
      level: -1,
      order: 0,
      operator: 'AND',
      days: null,
      months: null,
      durationType: null,
      date: null,
      comparator: 'LESS_THAN_OR_EQUAL',
      displayValue: 'less than or equals 0 days and 0 months',
      headerPlaceholder: 'Configure mobile login',
    },
    apiKeys: [
      'selectionType',
      'level',
      'operator',
      'days',
      'months',
      'durationType',
      'date',
      'comparator',
    ],
  },
  {
    selectionType: 'MEMBER_CLUBS',
    label: 'Clubs',
    category: FilterCategory.OTHER,
    defaults: {
      level: -1,
      order: 0,
      operator: 'AND',
      clubIds: [],
      displayValue: 'Select clubs',
      headerPlaceholder: 'Select clubs',
    },
    apiKeys: ['selectionType', 'clubIds', 'level', 'operator'],
  },
  {
    selectionType: 'MEMBER_INVOICE_INSTORE',
    label: 'Member instore invoice',
    category: FilterCategory.TRANSACTION,
    defaults: {
      level: -1,
      order: 0,
      operator: 'AND',
      useDateRange: false,
      invoiceDateFrom: null,
      invoiceDateUntil: null,
      comperator: 'GREATER_THAN',
      days: 0,
      months: 0,
      storeIds: [],
      displayValue: 'more than 0 days and 0 months',
      headerPlaceholder: 'Configure invoice instore',
    },
    apiKeys: [
      'selectionType',
      'level',
      'operator',
      'useDateRange',
      'invoiceDateFrom',
      'invoiceDateUntil',
      'comperator',
      'days',
      'months',
      'storeIds',
    ],
  },
  {
    selectionType: 'MEMBER_IN_TAG',
    label: 'Tag',
    category: FilterCategory.OTHER,
    defaults: {
      level: -1,
      order: 0,
      operator: 'AND',
      tagId: null,
      tagDescription: '',
      durationType: null,
      days: null,
      months: null,
      dateFrom: null,
      displayValue: 'Choose a tag',
      headerPlaceholder: 'Choose a tag',
    },
    apiKeys: [
      'selectionType',
      'level',
      'operator',
      'tagId',
      'tagDescription',
      'durationType',
      'days',
      'months',
      'dateFrom',
    ],
  },
  {
    selectionType: 'MEMBER_PREFERRED_STORE',
    label: 'Preferred store',
    category: FilterCategory.PERSONAL,
    defaults: {
      level: -1,
      order: 0,
      operator: 'AND',
      storeIds: [],
      displayValue: 'Select preferred stores',
      headerPlaceholder: 'Select preferred stores',
    },
    apiKeys: ['selectionType', 'level', 'operator', 'storeIds'],
  },
  {
    selectionType: 'MEMBER_NOTIFICATION_DEVICE',
    label: 'Member notification device',
    category: FilterCategory.PERSONAL,
    defaults: {
      level: -1,
      order: 0,
      operator: 'AND',
      displayValue: 'Enabled',
      headerPlaceholder: 'Enabled',
    },
    apiKeys: ['selectionType', 'level', 'operator'],
  },
  {
    selectionType: 'MEMBER_MAX_CAMPAIGN',
    label: 'Have received a number of campaigns',
    category: FilterCategory.CAMPAIGN,
    defaults: {
      level: -1,
      order: 0,
      operator: 'AND',
      communicationChannels: [],
      comparator: null,
      currentDate: null,
      dateFrom: null,
      dateUntil: null,
      campaignExecutionCount: 1,
      displayValue: 'Configure campaign filter',
      headerPlaceholder: 'Configure campaign filter',
    },
    apiKeys: [
      'selectionType',
      'level',
      'operator',
      'communicationChannels',
      'comparator',
      'currentDate',
      'dateFrom',
      'dateUntil',
      'campaignExecutionCount',
    ],
  },
  {
    selectionType: 'MEMBER_CLUB_EXPIRATION',
    label: 'Member Club Expiration',
    category: FilterCategory.OTHER,
    defaults: {
      level: -1,
      order: 0,
      operator: 'AND',
      clubIds: [],
      fromDays: 0,
      fromMonths: 0,
      fromExpirationDate: null,
      untilDays: 0,
      untilMonths: 0,
      untilExpirationDate: null,
      displayValue: 'Select clubs',
      headerPlaceholder: 'Select clubs',
    },
    apiKeys: [
      'selectionType',
      'level',
      'operator',
      'clubIds',
      'fromDays',
      'fromMonths',
      'fromExpirationDate',
      'untilDays',
      'untilMonths',
      'untilExpirationDate',
    ],
  },
  {
    selectionType: 'MEMBER_ACTIVE_COUPON',
    label: 'Member Active Coupon',
    category: FilterCategory.COUPON,
    defaults: {
      level: -1,
      order: 0,
      operator: 'AND',
      couponSetIdList: [],
      from: { days: null, months: null, date: null },
      to: { days: null, months: null, date: null },
      displayValue: 'Select coupon sets',
      headerPlaceholder: 'Select coupon sets',
    },
    apiKeys: ['selectionType', 'level', 'operator', 'couponSetIdList', 'from', 'to'],
  },
  {
    selectionType: 'MEMBER_IN_TIER',
    label: 'Tiers',
    category: FilterCategory.OTHER,
    defaults: {
      level: -1,
      order: 0,
      operator: 'AND',
      tierList: [],
      displayValue: 'Select tiers',
      headerPlaceholder: 'Select tiers',
    },
    apiKeys: ['selectionType', 'level', 'operator', 'tierList'],
  },
  {
    selectionType: 'MEMBER_ASSIGNED_TAG',
    label: 'Tag (lifetime)',
    category: FilterCategory.OTHER,
    defaults: {
      level: -1,
      order: 0,
      operator: 'AND',
      tagId: null,
      tagDescription: '',
      displayValue: 'Choose a tag',
      headerPlaceholder: 'Choose a tag',
    },
    apiKeys: ['selectionType', 'level', 'operator', 'tagId', 'tagDescription'],
  },
  {
    selectionType: 'MEMBER_POINTLOGS_THRESHOLD',
    label: 'Member Pointlogs',
    category: FilterCategory.OTHER,
    defaults: {
      level: -1,
      order: 0,
      operator: 'AND',
      days: 0,
      months: 0,
      pointsDateFrom: null,
      pointsDateUntil: null,
      pointsThreshold: 0,
      pointsComperator: null,
      dateComperator: 'GREATER_THAN_OR_EQUAL',
      useDateRange: false,
      displayValue: 'Configure pointlogs threshold',
      headerPlaceholder: 'Configure pointlogs threshold',
    },
    apiKeys: [
      'selectionType',
      'level',
      'operator',
      'days',
      'months',
      'pointsDateFrom',
      'pointsDateUntil',
      'pointsThreshold',
      'pointsComperator',
      'dateComperator',
      'useDateRange',
    ],
  },
  {
    selectionType: 'MEMBER_RFM_ANALYSIS',
    label: 'RFM Analysis',
    category: FilterCategory.OTHER,
    defaults: {
      level: -1,
      order: 0,
      operator: 'AND',
      tableRFMIds: [],
      displayValue: 'Select RFM segments',
      headerPlaceholder: 'Select RFM segments',
    },
    apiKeys: ['selectionType', 'level', 'operator', 'tableRFMIds'],
  },
  {
    selectionType: 'MEMBER_EXPIRED',
    label: 'Member Expired',
    category: FilterCategory.OTHER,
    defaults: {
      level: -1,
      order: 0,
      operator: 'AND',
      displayValue: 'Enabled',
      headerPlaceholder: 'Enabled',
    },
    apiKeys: ['selectionType', 'level', 'operator'],
  },
  {
    selectionType: 'MEMBER_STORE_VISIT_INACTIVE',
    label: 'No check in',
    category: FilterCategory.OTHER,
    defaults: {
      level: -1,
      order: 0,
      operator: 'AND',
      days: null,
      months: null,
      displayValue: 'Set days or months',
      headerPlaceholder: 'Set days or months',
    },
    apiKeys: ['selectionType', 'level', 'operator', 'days', 'months'],
  },
  {
    selectionType: 'MEMBER_PLASTIC_LOYALTYCARD',
    label: 'Plastic loyalty card',
    category: FilterCategory.OTHER,
    defaults: {
      level: -1,
      order: 0,
      operator: 'AND',
      fromDays: 0,
      fromMonths: 0,
      fromCardBatchDay: null,
      untilDays: 0,
      untilMonths: 0,
      untilCardBatchDay: null,
      displayValue: 'Configure card batch creation',
      headerPlaceholder: 'Configure card batch creation',
    },
    apiKeys: [
      'selectionType',
      'level',
      'operator',
      'fromDays',
      'fromMonths',
      'fromCardBatchDay',
      'untilDays',
      'untilMonths',
      'untilCardBatchDay',
    ],
  },
  {
    selectionType: 'MEMBER_LOYALTYCARD_TYPE',
    label: 'Loyalty card type',
    category: FilterCategory.OTHER,
    defaults: {
      level: -1,
      order: 0,
      operator: 'AND',
      cardType: 'PERMANENT',
      displayValue: 'PERMANENT',
      headerPlaceholder: 'Select card type',
    },
    apiKeys: ['selectionType', 'level', 'operator', 'cardType'],
  },
  {
    selectionType: 'MEMBER_VALID_PERSONALIZE',
    label: 'Valid personalize',
    category: FilterCategory.PERSONAL,
    defaults: {
      level: -1,
      order: 0,
      operator: 'AND',
      displayValue: 'Enabled',
      headerPlaceholder: 'Enabled',
    },
    apiKeys: ['selectionType', 'level', 'operator'],
  },
  {
    selectionType: 'MEMBER_CHILDREN_COUNT',
    label: 'Number of children',
    category: FilterCategory.PERSONAL,
    defaults: {
      level: -1,
      order: 0,
      operator: 'AND',
      childrenCount: null,
      comperator: 'GREATER_THAN',
      displayValue: 'more than 0 child/children',
      headerPlaceholder: 'Set children count',
    },
    apiKeys: ['selectionType', 'level', 'operator', 'childrenCount', 'comperator'],
  },
  {
    selectionType: 'MEMBER_DATA_ENTRY_DATE',
    label: 'Member data entry date',
    category: FilterCategory.OTHER,
    defaults: {
      level: -1,
      order: 0,
      operator: 'AND',
      days: 0,
      months: 0,
      durationType: null,
      dataEntryDate: null,
      comparator: 'GREATER_THAN',
      displayValue: 'more than 0 days and 0 months',
      headerPlaceholder: 'Configure data entry date',
    },
    apiKeys: [
      'selectionType',
      'level',
      'operator',
      'days',
      'months',
      'durationType',
      'dataEntryDate',
      'comparator',
    ],
  },
  {
    selectionType: 'MEMBER_CSI_SCORE',
    label: 'CSI Average Score',
    category: FilterCategory.OTHER,
    defaults: {
      level: -1,
      order: 0,
      operator: 'AND',
      min: 0,
      max: 0,
      displayValue: 'Score between 0 and 0',
      headerPlaceholder: 'Set CSI score range',
    },
    apiKeys: ['selectionType', 'level', 'operator', 'min', 'max'],
  },
  {
    selectionType: 'MEMBER_MTV_ANALYSIS',
    label: 'MTV Analysis',
    category: FilterCategory.OTHER,
    defaults: {
      level: -1,
      order: 0,
      operator: 'AND',
      tableMTVIds: [],
      displayValue: 'Select MTV segments',
      headerPlaceholder: 'Select MTV segments',
    },
    apiKeys: ['selectionType', 'level', 'operator', 'tableMTVIds'],
  },
  {
    selectionType: 'MEMBER_CAMPAIGN_EMAIL_REPORT_CLICKED',
    label: 'Campaign email reporting (clicked)',
    category: FilterCategory.CAMPAIGN,
    defaults: {
      level: -1,
      order: 0,
      operator: 'AND',
      days: null,
      months: null,
      durationType: null,
      executionDate: null,
      comparator: 'LESS_THAN_OR_EQUAL',
      campaignIds: [],
      displayValue: 'Configure campaign reporting',
      headerPlaceholder: 'Configure campaign reporting',
    },
    apiKeys: [
      'selectionType',
      'level',
      'operator',
      'days',
      'months',
      'durationType',
      'executionDate',
      'comparator',
      'campaignIds',
    ],
  },
  {
    selectionType: 'MEMBER_CAMPAIGN_EMAIL_REPORT_OPENED',
    label: 'Campaign email reporting (opened)',
    category: FilterCategory.CAMPAIGN,
    defaults: {
      level: -1,
      order: 0,
      operator: 'AND',
      days: null,
      months: null,
      durationType: null,
      executionDate: null,
      comparator: 'LESS_THAN_OR_EQUAL',
      campaignIds: [],
      displayValue: 'Configure campaign reporting',
      headerPlaceholder: 'Configure campaign reporting',
    },
    apiKeys: [
      'selectionType',
      'level',
      'operator',
      'days',
      'months',
      'durationType',
      'executionDate',
      'comparator',
      'campaignIds',
    ],
  },
  {
    selectionType: 'MEMBER_CAMPAIGN_INAPP_REPORT_CLICKED',
    label: 'Campaign in-app reporting (clicked)',
    category: FilterCategory.CAMPAIGN,
    defaults: {
      level: -1,
      order: 0,
      operator: 'AND',
      days: null,
      months: null,
      durationType: null,
      executionDate: null,
      comparator: 'LESS_THAN_OR_EQUAL',
      campaignIds: [],
      displayValue: 'Configure campaign reporting',
      headerPlaceholder: 'Configure campaign reporting',
    },
    apiKeys: [
      'selectionType',
      'level',
      'operator',
      'days',
      'months',
      'durationType',
      'executionDate',
      'comparator',
      'campaignIds',
    ],
  },
  {
    selectionType: 'MEMBER_CAMPAIGN_INAPP_REPORT_OPENED',
    label: 'Campaign in-app reporting (opened)',
    category: FilterCategory.CAMPAIGN,
    defaults: {
      level: -1,
      order: 0,
      operator: 'AND',
      days: null,
      months: null,
      durationType: null,
      executionDate: null,
      comparator: 'LESS_THAN_OR_EQUAL',
      campaignIds: [],
      displayValue: 'Configure campaign reporting',
      headerPlaceholder: 'Configure campaign reporting',
    },
    apiKeys: [
      'selectionType',
      'level',
      'operator',
      'days',
      'months',
      'durationType',
      'executionDate',
      'comparator',
      'campaignIds',
    ],
  },
  {
    selectionType: 'MEMBER_CAMPAIGN_PUSH_REPORT_CLICKED',
    label: 'Campaign push reporting (clicked)',
    category: FilterCategory.CAMPAIGN,
    defaults: {
      level: -1,
      order: 0,
      operator: 'AND',
      days: null,
      months: null,
      durationType: null,
      executionDate: null,
      comparator: 'LESS_THAN_OR_EQUAL',
      campaignIds: [],
      displayValue: 'Configure campaign reporting',
      headerPlaceholder: 'Configure campaign reporting',
    },
    apiKeys: [
      'selectionType',
      'level',
      'operator',
      'days',
      'months',
      'durationType',
      'executionDate',
      'comparator',
      'campaignIds',
    ],
  },
  {
    selectionType: 'MEMBER_INVOICE_INREGION',
    label: 'Member inregion invoice',
    category: FilterCategory.TRANSACTION,
    defaults: {
      level: -1,
      order: 0,
      operator: 'AND',
      useDateRange: false,
      invoiceDateFrom: null,
      invoiceDateUntil: null,
      comperator: 'GREATER_THAN',
      days: 0,
      months: 0,
      countryIdReffered: null,
      regionIds: [],
      displayValue: 'more than 0 days and 0 months',
      headerPlaceholder: 'Configure inregion invoice',
    },
    apiKeys: [
      'selectionType',
      'level',
      'operator',
      'useDateRange',
      'invoiceDateFrom',
      'invoiceDateUntil',
      'comperator',
      'days',
      'months',
      'countryIdReffered',
      'regionIds',
    ],
  },
  {
    selectionType: 'MEMBER_TIER_EVALUATION_DATE',
    label: 'Tier Reevaluation',
    category: FilterCategory.OTHER,
    defaults: {
      level: -1,
      order: 0,
      operator: 'AND',
      tierList: [],
      from: { days: null, months: null, date: null },
      to: { days: null, months: null, date: null },
      displayValue: 'Configure tier reevaluation',
      headerPlaceholder: 'Configure tier reevaluation',
    },
    apiKeys: ['selectionType', 'level', 'operator', 'tierList', 'from', 'to'],
  },
  {
    selectionType: 'PRODUCT_CATEGORY',
    label: 'Product Category',
    category: FilterCategory.TRANSACTION,
    defaults: {
      level: -1,
      order: 0,
      operator: 'AND',
      productCategoryLevel: null,
      productCategory: null,
      languageCode: null,
      from: { days: null, months: null, date: null },
      to: { days: null, months: null, date: null },
      displayValue: 'Configure product category',
      headerPlaceholder: 'Configure product category',
    },
    apiKeys: [
      'selectionType',
      'level',
      'operator',
      'productCategoryLevel',
      'productCategory',
      'languageCode',
      'from',
      'to',
    ],
  },
  {
    selectionType: 'MEMBER_COUNTRY_POSTCODE',
    label: 'Member country postcode',
    category: FilterCategory.PERSONAL,
    defaults: {
      level: 0,
      order: 0,
      operator: 'AND',
      postCodeList: [],
      countryId: null,
      displayValue: 'Select country and postcodes',
      headerPlaceholder: 'Select country and postcodes',
    },
    apiKeys: ['selectionType', 'level', 'operator', 'postCodeList', 'countryId'],
  },
  {
    selectionType: 'MEMBER_PERSON_SOURCE',
    label: 'Person source',
    category: FilterCategory.PERSONAL,
    defaults: {
      level: -1,
      order: 0,
      operator: 'AND',
      personSourceIdList: [],
      displayValue: 'Select person sources',
      headerPlaceholder: 'Select person sources',
    },
    apiKeys: ['selectionType', 'level', 'operator', 'personSourceIdList'],
  },
  {
    selectionType: 'MEMBER_INVOICE_ATTRIBUTES',
    label: 'Invoice Attributes',
    category: FilterCategory.TRANSACTION,
    defaults: {
      level: -1,
      order: 0,
      operator: 'AND',
      attributeFilters: [{ attributeType: null, attributeName: null, attributeValue: null }],
      displayValue: 'Choose attributes',
      headerPlaceholder: 'Choose attributes',
    },
    apiKeys: ['selectionType', 'level', 'operator', 'attributeFilters'],
  },
  {
    selectionType: 'MEMBER_GENERIC_ACTIVITY',
    label: 'Member Activity',
    category: FilterCategory.OTHER,
    defaults: {
      level: -1,
      order: 0,
      operator: 'AND',
      subType: null,
      extraFilters: {},
      dateFrom: null,
      dateUntil: null,
      days: 0,
      months: 0,
      durationType: null,
      displayValue: 'Configure activity',
      headerPlaceholder: 'Configure activity',
    },
    apiKeys: [
      'selectionType',
      'level',
      'operator',
      'subType',
      'extraFilters',
      'dateFrom',
      'dateUntil',
      'days',
      'months',
    ],
  },
  {
    selectionType: 'MEMBER_COUNTRY_REGION',
    label: 'Country & Region',
    category: FilterCategory.PERSONAL,
    defaults: {
      level: -1,
      order: 0,
      operator: 'AND',
      countryId: null,
      regionIds: [],
      displayValue: 'Select country and regions',
      headerPlaceholder: 'Select country and regions',
    },
    apiKeys: ['selectionType', 'level', 'operator', 'countryId', 'regionIds'],
  },
  {
    selectionType: 'MEMBER_MULTIPLE_CAMPAIGN_EXCLUDE',
    label: 'Campaign participants',
    category: FilterCategory.CAMPAIGN,
    defaults: {
      level: -1,
      order: 0,
      operator: 'AND',
      days: null,
      months: null,
      year: false,
      month: false,
      searchCampaignExecutionsBeforeTheCalculatedDate: false,
      excludedCampaignIds: null,
      thisCampaign: false,
      savedFilter: {
        campaignName: null,
        campaignCategoryId: null,
        deactivate: false,
        user: false,
        notes: null,
        campaignChannel: null,
      },
      displayValue: 'Configuring...',
      headerPlaceholder: 'Configuring...',
    },
    apiKeys: [
      'selectionType',
      'level',
      'operator',
      'days',
      'months',
      'year',
      'month',
      'searchCampaignExecutionsBeforeTheCalculatedDate',
      'excludedCampaignIds',
      'thisCampaign',
      'savedFilter',
    ],
  },
  {
    selectionType: 'MEMBER_PERSON_NOTIFICATION_DEVICE',
    label: 'Person Notification Device',
    category: FilterCategory.PERSONAL,
    defaults: {
      level: -1,
      order: 0,
      operator: 'AND',
      category: 'PERSONAL',
      displayValue: 'Enabled',
      headerPlaceholder: 'Enabled',
    },
    apiKeys: ['selectionType', 'level', 'operator'],
  },
  {
    selectionType: 'MEMBER_SOCIAL_MEDIA_PAGE_LIKES',
    label: 'Member Social Media Page Likes',
    category: FilterCategory.OTHER,
    defaults: {
      level: -1,
      operator: 'AND',
      socialMediaMetaDataId: '',
      days: 0,
      months: 0,
      dateFrom: null,
      durationType: null,
      siriusAccountId: null,
      provider: null,
      category: 'OTHER',
      displayValue: 'Select account and timeframe',
    },
    apiKeys: [
      'selectionType',
      'level',
      'operator',
      'siriusAccountId',
      'provider',
      'socialMediaMetaDataId',
      'durationType',
      'days',
      'months',
      'dateFrom',
    ],
  },
];

export const PERSON_TEMPLATES_DATA: FilterTemplateDefinition[] = [
  {
    selectionType: 'PERSON_AGE',
    label: 'Person Age',
    category: FilterCategory.PERSONAL,
    defaults: {
      operator: 'AND',
      level: -1,
      order: 0,
      minAge: null,
      maxAge: null,
      displayFormat: 'Age between {minAge} and {maxAge}',
      headerPlaceholder: 'Choose age range',
    },
    apiKeys: ['selectionType', 'level', 'operator', 'minAge', 'maxAge'],
  },
  {
    selectionType: 'PERSON_CHILD_BIRTHDAY',
    label: 'Child Birthdate',
    category: FilterCategory.PERSONAL,
    defaults: {
      level: -1,
      order: 0,
      operator: 'AND',
      daysAfter: 0,
      maxChildsAge: 18,
      displayFormat: "{daysAfter} after/before/on the child's birthday",
      headerPlaceholder: "On the child's birthday",
    },
    apiKeys: ['maxChildsAge', 'selectionType', 'level', 'operator', 'daysAfter'],
  },
  {
    selectionType: 'PERSON_BIRTHDATE',
    label: 'Person Birthdate',
    category: FilterCategory.PERSONAL,
    defaults: {
      level: -1,
      order: 0,
      operator: 'AND',
      daysAfter: 0,
      displayFormat: "On the person's birthday",
      headerPlaceholder: "On the person's birthday",
    },
    apiKeys: ['selectionType', 'level', 'operator', 'daysAfter'],
  },
  {
    selectionType: 'PERSON_GENDER_SELECTION',
    label: 'Gender',
    category: FilterCategory.PERSONAL,
    defaults: {
      level: -1,
      order: 0,
      operator: 'AND',
      genderList: [],
      displayValue: 'Choose a gender type',
      headerPlaceholder: 'Choose a gender type',
    },
    apiKeys: ['selectionType', 'level', 'operator', 'genderList'],
  },
  {
    selectionType: 'PERSON_VALID_EMAIL',
    label: 'Valid e-mail',
    category: FilterCategory.PERSONAL,
    defaults: {
      level: -1,
      order: 0,
      operator: 'AND',
      displayValue: 'Enabled',
      headerPlaceholder: 'Enabled',
    },
    apiKeys: ['selectionType', 'level', 'operator'],
  },
  {
    selectionType: 'PERSON_VALID_MOBILE',
    label: 'Valid mobile',
    category: FilterCategory.PERSONAL,
    defaults: {
      level: -1,
      order: 0,
      operator: 'AND',
      displayValue: 'Enabled',
      headerPlaceholder: 'Enabled',
    },
    apiKeys: ['selectionType', 'level', 'operator'],
  },
  {
    selectionType: 'PERSON_ATTRIBUTES',
    label: 'Attributes',
    category: FilterCategory.OTHER,
    defaults: {
      level: -1,
      order: 0,
      operator: 'AND',
      attributeFilters: [{ attributeType: null, attributeName: null, attributeValue: null }],
      displayValue: 'Choose attributes',
      headerPlaceholder: 'Choose attributes',
    },
    apiKeys: ['selectionType', 'level', 'operator', 'attributeFilters'],
  },
  {
    selectionType: 'PERSON_INVOICE',
    label: 'Invoice',
    category: FilterCategory.TRANSACTION,
    defaults: {
      level: -1,
      order: 0,
      operator: 'LESS_THAN_OR_EQUAL',
      comperator: null,
      durationType: null,
      days: null,
      months: null,
      invoiceDate: null,
      invoiceFields: [{ fieldName: null, value: null }],
      displayValue: 'Choose invoice fields',
      headerPlaceholder: 'Choose invoice fields',
    },
    apiKeys: [
      'selectionType',
      'level',
      'operator',
      'comperator',
      'durationType',
      'days',
      'months',
      'invoiceDate',
      'invoiceFields',
    ],
  },
  {
    selectionType: 'PERSON_UI_PRODUCT',
    label: 'Product relation start date',
    category: FilterCategory.OTHER,
    defaults: {
      level: -1,
      order: 0,
      operator: 'AND',
      comperator: 'LESS_THAN_OR_EQUAL',
      durationType: null,
      days: 0,
      months: 0,
      relationStartDate: null,
      displayValue: 'Define a time period',
      headerPlaceholder: 'Define a time period',
    },
    apiKeys: [
      'selectionType',
      'level',
      'operator',
      'comperator',
      'durationType',
      'days',
      'months',
      'relationStartDate',
    ],
  },
  {
    selectionType: 'PERSON_NAMED_FLAG',
    label: 'Consents',
    category: FilterCategory.PERSONAL,
    defaults: {
      level: -1,
      order: 0,
      operator: 'AND',
      flag: false,
      namedFlag: null,
      flagChannel: null,
      displayValue: 'Choose a consent',
      headerPlaceholder: 'Choose a consent',
    },
    apiKeys: ['selectionType', 'level', 'operator', 'flag', 'namedFlag', 'flagChannel'],
  },
  {
    selectionType: 'PERSON_PREFERRED_LANGUAGE',
    label: 'Preferred language',
    category: FilterCategory.PERSONAL,
    defaults: {
      level: -1,
      order: 0,
      operator: 'AND',
      languageIds: [],
      displayValue: 'Select languages',
      headerPlaceholder: 'Select languages',
    },
    apiKeys: ['selectionType', 'languageIds', 'level', 'operator'],
  },
  {
    selectionType: 'PERSON_IN_CAMPAIGNS',
    label: 'Campaign participants',
    category: FilterCategory.CAMPAIGN,
    defaults: {
      level: -1,
      order: 0,
      operator: 'AND',
      days: null,
      months: null,
      year: false,
      month: false,
      searchCampaignExecutionsBeforeTheCalculatedDate: false,
      excludedCampaignIds: null,
      thisCampaign: false,
      savedFilter: {
        campaignName: null,
        campaignCategoryId: null,
        deactivate: null,
        user: null,
        notes: null,
        campaignChannel: null,
      },
      displayValue: 'Configuring...',
      headerPlaceholder: 'Configuring...',
    },
    apiKeys: [
      'selectionType',
      'level',
      'operator',
      'days',
      'months',
      'year',
      'month',
      'searchCampaignExecutionsBeforeTheCalculatedDate',
      'excludedCampaignIds',
      'thisCampaign',
      'savedFilter',
    ],
  },
  {
    selectionType: 'PERSON_IN_TAG',
    label: 'Tag',
    category: FilterCategory.OTHER,
    defaults: {
      level: -1,
      order: 0,
      operator: 'AND',
      tagId: null,
      tagDescription: '',
      durationType: null,
      days: null,
      months: null,
      dateFrom: null,
      displayValue: 'Choose a tag',
      headerPlaceholder: 'Choose a tag',
    },
    apiKeys: [
      'selectionType',
      'level',
      'operator',
      'tagId',
      'tagDescription',
      'durationType',
      'days',
      'months',
      'dateFrom',
    ],
  },
  {
    selectionType: 'PERSON_ASSIGNED_TAG',
    label: 'Tag (lifetime)',
    category: FilterCategory.OTHER,
    defaults: {
      level: -1,
      order: 0,
      operator: 'AND',
      tagId: null,
      tagDescription: '',
      displayValue: 'Choose a tag',
      headerPlaceholder: 'Choose a tag',
    },
    apiKeys: ['selectionType', 'level', 'operator', 'tagId', 'tagDescription'],
  },
  {
    selectionType: 'PERSON_INVOICE_INSTORE',
    label: 'Person instore invoice',
    category: FilterCategory.TRANSACTION,
    defaults: {
      level: -1,
      order: 0,
      operator: 'AND',
      useDateRange: false,
      invoiceDateFrom: null,
      invoiceDateUntil: null,
      comperator: 'GREATER_THAN',
      days: 0,
      months: 0,
      storeIds: [],
      displayValue: 'more than 0 days and 0 months',
      headerPlaceholder: 'Configure invoice instore',
    },
    apiKeys: [
      'selectionType',
      'level',
      'operator',
      'useDateRange',
      'invoiceDateFrom',
      'invoiceDateUntil',
      'comperator',
      'days',
      'months',
      'storeIds',
    ],
  },
  {
    selectionType: 'PERSON_COUNTRY_REGION',
    label: 'Country & Region',
    category: FilterCategory.PERSONAL,
    defaults: {
      level: -1,
      order: 0,
      operator: 'AND',
      countryId: null,
      regionIds: [],
      displayValue: 'Select country and regions',
      headerPlaceholder: 'Select country and regions',
    },
    apiKeys: ['selectionType', 'level', 'operator', 'countryId', 'regionIds'],
  },
  {
    selectionType: 'PERSON_CHILDREN_COUNT',
    label: 'Number of children',
    category: FilterCategory.PERSONAL,
    defaults: {
      level: -1,
      order: 0,
      operator: 'AND',
      childrenCount: null,
      comperator: 'GREATER_THAN',
      displayValue: 'more than 0 child/children',
      headerPlaceholder: 'Set children count',
    },
    apiKeys: ['selectionType', 'level', 'operator', 'childrenCount', 'comperator'],
  },
  {
    selectionType: 'PERSON_REGISTRATION_DATE',
    label: 'Registration date',
    category: FilterCategory.PERSONAL,
    defaults: {
      level: -1,
      order: 0,
      operator: 'AND',
      days: 0,
      months: 0,
      registrationDate: null,
      comperator: 'GREATER_THAN',
      durationType: 'daysMonths',
      displayValue: 'more than 0 days and 0 months',
      headerPlaceholder: 'more than 0 days and 0 months',
    },
    apiKeys: [
      'selectionType',
      'level',
      'operator',
      'days',
      'months',
      'registrationDate',
      'comperator',
    ],
  },
  {
    selectionType: 'PERSON_REGISTRATION_STORE',
    label: 'Registration store',
    category: FilterCategory.OTHER,
    defaults: {
      level: -1,
      order: 0,
      operator: 'AND',
      registrationStoreId: null,
      displayValue: 'Select a store',
      headerPlaceholder: 'Select a store',
    },
    apiKeys: ['selectionType', 'level', 'operator', 'registrationStoreId'],
  },
  {
    selectionType: 'PERSON_PERSON_SOURCE',
    label: 'Person source',
    category: FilterCategory.PERSONAL,
    defaults: {
      level: -1,
      order: 0,
      operator: 'AND',
      personSourceIdList: [],
      displayValue: 'Select person sources',
      headerPlaceholder: 'Select person sources',
    },
    apiKeys: ['selectionType', 'level', 'operator', 'personSourceIdList'],
  },
  {
    selectionType: 'PERSON_CAMPAIGN_EMAIL_REPORT_CLICKED',
    label: 'Campaign email reporting (clicked)',
    category: FilterCategory.CAMPAIGN,
    defaults: {
      level: -1,
      order: 0,
      operator: 'AND',
      days: null,
      months: null,
      durationType: null,
      executionDate: null,
      comparator: 'LESS_THAN_OR_EQUAL',
      campaignIds: [],
      displayValue: 'Configure campaign reporting',
      headerPlaceholder: 'Configure campaign reporting',
    },
    apiKeys: [
      'selectionType',
      'level',
      'operator',
      'days',
      'months',
      'durationType',
      'executionDate',
      'comparator',
      'campaignIds',
    ],
  },
  {
    selectionType: 'PERSON_CAMPAIGN_EMAIL_REPORT_OPENED',
    label: 'Campaign email reporting (opened)',
    category: FilterCategory.CAMPAIGN,
    defaults: {
      level: -1,
      order: 0,
      operator: 'AND',
      days: null,
      months: null,
      durationType: null,
      executionDate: null,
      comparator: 'LESS_THAN_OR_EQUAL',
      campaignIds: [],
      displayValue: 'Configure campaign reporting',
      headerPlaceholder: 'Configure campaign reporting',
    },
    apiKeys: [
      'selectionType',
      'level',
      'operator',
      'days',
      'months',
      'durationType',
      'executionDate',
      'comparator',
      'campaignIds',
    ],
  },
  {
    selectionType: 'PERSON_CAMPAIGN_INAPP_REPORT_CLICKED',
    label: 'Campaign in-app reporting (clicked)',
    category: FilterCategory.CAMPAIGN,
    defaults: {
      level: -1,
      order: 0,
      operator: 'AND',
      days: null,
      months: null,
      durationType: null,
      executionDate: null,
      comparator: 'LESS_THAN_OR_EQUAL',
      campaignIds: [],
      displayValue: 'Configure campaign reporting',
      headerPlaceholder: 'Configure campaign reporting',
    },
    apiKeys: [
      'selectionType',
      'level',
      'operator',
      'days',
      'months',
      'durationType',
      'executionDate',
      'comparator',
      'campaignIds',
    ],
  },
  {
    selectionType: 'PERSON_CAMPAIGN_INAPP_REPORT_OPENED',
    label: 'Campaign in-app reporting (opened)',
    category: FilterCategory.CAMPAIGN,
    defaults: {
      level: -1,
      order: 0,
      operator: 'AND',
      days: null,
      months: null,
      durationType: null,
      executionDate: null,
      comparator: 'LESS_THAN_OR_EQUAL',
      campaignIds: [],
      displayValue: 'Configure campaign reporting',
      headerPlaceholder: 'Configure campaign reporting',
    },
    apiKeys: [
      'selectionType',
      'level',
      'operator',
      'days',
      'months',
      'durationType',
      'executionDate',
      'comparator',
      'campaignIds',
    ],
  },
  {
    selectionType: 'PERSON_CAMPAIGN_PUSH_REPORT_CLICKED',
    label: 'Campaign push reporting (clicked)',
    category: FilterCategory.CAMPAIGN,
    defaults: {
      level: -1,
      order: 0,
      operator: 'AND',
      days: null,
      months: null,
      durationType: null,
      executionDate: null,
      comparator: 'LESS_THAN_OR_EQUAL',
      campaignIds: [],
      displayValue: 'Configure campaign reporting',
      headerPlaceholder: 'Configure campaign reporting',
    },
    apiKeys: [
      'selectionType',
      'level',
      'operator',
      'days',
      'months',
      'durationType',
      'executionDate',
      'comparator',
      'campaignIds',
    ],
  },
  {
    selectionType: 'PERSON_MAX_CAMPAIGN',
    label: 'Have received a number of campaigns',
    category: FilterCategory.CAMPAIGN,
    defaults: {
      level: -1,
      order: 0,
      operator: 'AND',
      communicationChannels: [],
      comparator: null,
      currentDate: null,
      dateFrom: null,
      dateUntil: null,
      campaignExecutionCount: 1,
      displayValue: 'Configure campaign filter',
      headerPlaceholder: 'Configure campaign filter',
    },
    apiKeys: [
      'selectionType',
      'level',
      'operator',
      'communicationChannels',
      'comparator',
      'currentDate',
      'dateFrom',
      'dateUntil',
      'campaignExecutionCount',
    ],
  },
  {
    selectionType: 'PERSON_BOUNCED_EMAIL',
    label: 'Bounced e-mail',
    category: FilterCategory.PERSONAL,
    defaults: {
      level: -1,
      order: 0,
      operator: 'AND',
      displayValue: 'Enabled',
      headerPlaceholder: 'Enabled',
    },
    apiKeys: ['selectionType', 'level', 'operator'],
  },
  {
    selectionType: 'PERSON_CSI_SCORE',
    label: 'CSI Average Score',
    category: FilterCategory.OTHER,
    defaults: {
      level: -1,
      order: 0,
      operator: 'AND',
      min: 0,
      max: 0,
      displayValue: 'Score between 0 and 0',
      headerPlaceholder: 'Set CSI score range',
    },
    apiKeys: ['selectionType', 'level', 'operator', 'min', 'max'],
  },
  {
    selectionType: 'PERSON_INVOICE_INACTIVE',
    label: 'No transaction',
    category: FilterCategory.TRANSACTION,
    defaults: {
      level: -1,
      order: 0,
      operator: 'AND',
      days: null,
      months: null,
      storeOperator: 'ALL',
      storeIds: [],
      displayValue: 'Set days or months',
      headerPlaceholder: 'Set days or months',
    },
    apiKeys: ['selectionType', 'level', 'operator', 'days', 'months', 'storeOperator', 'storeIds'],
  },
  {
    selectionType: 'PERSON_PREFERRED_STORE',
    label: 'Preferred store',
    category: FilterCategory.PERSONAL,
    defaults: {
      level: -1,
      order: 0,
      operator: 'AND',
      storeIds: [],
      displayValue: 'Select preferred stores',
      headerPlaceholder: 'Select preferred stores',
    },
    apiKeys: ['selectionType', 'level', 'operator', 'storeIds'],
  },
  {
    selectionType: 'PERSON_NOTIFICATION_DEVICE',
    label: 'Person Notification Device',
    category: FilterCategory.PERSONAL,
    defaults: {
      level: -1,
      order: 0,
      operator: 'AND',
      category: 'PERSONAL',
      displayValue: 'Enabled',
      headerPlaceholder: 'Enabled',
    },
    apiKeys: ['selectionType', 'level', 'operator'],
  },
];

export const SHARED_TEMPLATES_DATA: FilterTemplateDefinition[] = [
  {
    selectionType: 'ADHOC_LIST',
    label: 'Custom List',
    category: FilterCategory.OTHER,
    defaults: {
      operator: 'AND',
      level: -1,
      order: 0,
      listName: '',
      displayFormat: '{listName}',
      headerPlaceholder: 'Choose an existing list',
    },
    apiKeys: ['selectionType', 'level', 'operator', 'listName'],
  },
];
